import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { useIsFocused } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { getSharedLineItemProductName } from '@order/shared-line-item';
import { useTelemetry } from '@order/Telemetry';

import { LocationSearch, NotFoundView, TransparentModal } from '../../components';
import { useLastInteractedStoreMachine } from '../../context';
import { useNavigateBack } from '../../navigation';

// ─────────────────────────────────────────────────────────────────────────────

export const ShareLineItemLocationScreen = () => {
  const isFocused = useIsFocused();

  const isSharedLinkCustomizationEnabled = useFeatureFlag('CELS-5505-order-shared-link-customization-enabled'); // prettier-ignore

  if (!isSharedLinkCustomizationEnabled) {
    return <NotFoundView />;
  }

  if (!isFocused) {
    return null;
  }

  return <ShareLineItemLocationScreenReady />;
};

// ─────────────────────────────────────────────────────────────────────────────

const ShareLineItemLocationScreenReady = () => {
  const router = useRouter();
  const navigateBack = useNavigateBack();
  const [, sendToLastInteractedStoreMachine] = useLastInteractedStoreMachine();
  const { track } = useTelemetry();

  const { baseProductSlug, lineItemSlug, sharedLineItemId } = useLocalSearchParams<RouteParams>();

  // ─── Derived ────────────────────────────────────────────────────────

  const sharedLineItemProductName = getSharedLineItemProductName({
    sharedLineItemSlug: lineItemSlug,
  });

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const navigateToCustomize = useCallback(() => {
    router.dismissTo({
      pathname: '/customize/[baseProductSlug]',
      params: {
        baseProductSlug,
        redirectTo: `/share/${lineItemSlug}`,
        sharedLineItemSlug: lineItemSlug,
        sharedLineItemId,
      },
    });
  }, [baseProductSlug, lineItemSlug, router, sharedLineItemId]);

  const navigateToLocation = useCallback<OnOpenRestaurant>(
    (context) => {
      if (!context.activeLocation) return;

      const { restaurantId, restaurantName, restaurantSlug } = context.activeLocation;

      sendToLastInteractedStoreMachine({ type: 'RESET_ACTIVE_CART_STORE' });
      sendToLastInteractedStoreMachine({
        type: 'SET_LAST_INTERACTED_MENU_STORE',
        store: { restaurantId, restaurantName, restaurantSlug },
      });

      track('share-line-item.location-change-success', {
        sharedLineItemProductName,
        sharedLineItemSlug: lineItemSlug,
        restaurantId,
        restaurantSlug,
      });
      navigateToCustomize();
    },
    [
      lineItemSlug,
      navigateToCustomize,
      sendToLastInteractedStoreMachine,
      sharedLineItemProductName,
      track,
    ],
  );

  const navigateToDeliveryLocation = useCallback<OnOpenDeliveryRestaurant>(
    (context) => {
      const { deliveryLocation } = context;

      if (!deliveryLocation) return;

      const {
        addressId,
        restaurantId,
        restaurantName,
        restaurantSlug,
        restaurantDeliveryFee,
        deliveryVendorName,
        deliveryVendorRestaurantId,
      } = deliveryLocation;

      sendToLastInteractedStoreMachine({ type: 'RESET_ACTIVE_CART_STORE' });
      sendToLastInteractedStoreMachine({
        type: 'SET_LAST_INTERACTED_MENU_STORE',
        store: {
          restaurantId,
          restaurantName,
          restaurantSlug,
          addressId,
          deliveryFee: restaurantDeliveryFee,
          vendor: deliveryVendorName,
          vendorRestaurantId: deliveryVendorRestaurantId,
        },
      });

      track('share-line-item.location-change-success', {
        addressId,
        sharedLineItemProductName,
        sharedLineItemSlug: lineItemSlug,
        restaurantId,
        restaurantSlug,
      });
      navigateToCustomize();
    },
    [
      lineItemSlug,
      navigateToCustomize,
      sendToLastInteractedStoreMachine,
      sharedLineItemProductName,
      track,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal size="large" offset="10%" onClose={handleDismiss}>
      <LocationSearch
        navigateToLocation={navigateToLocation}
        navigateToDeliveryLocation={navigateToDeliveryLocation}
        interactedLocationRestaurantSlug={undefined}
        interactedLocationDeliveryAddressId={undefined}
        isInsideModal
      />
    </TransparentModal>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  baseProductSlug: string;
  lineItemSlug: string;
  sharedLineItemId: string;
};

type LocationSearchProps = ComponentProps<typeof LocationSearch>;

type OnOpenRestaurant = NonNullable<LocationSearchProps['navigateToLocation']>;
type OnOpenDeliveryRestaurant = NonNullable<LocationSearchProps['navigateToDeliveryLocation']>;
